import type { PagedModelClientPost } from '@DigitaleDoerfer/digitale-doerfer-api';
import {
  GetAllPostsByGeoAreasForbiddenOrganizationTagsEnum,
  GetAllPostsByGeoAreasSortByEnum,
  GetAllPostsByGeoAreasTypesEnum
} from '@DigitaleDoerfer/digitale-doerfer-api';
import { useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';

import { API_CACHE_KEYS } from '@/features/api/constants/cache';
import { useGrapevinePostApi } from '@/features/api/hooks/grapevine-post/useGrapevinePostApi';
import { useAuth } from '@/features/authorization/provider/useAuth';
import { LAST_3_DAYS } from '@/features/posts/constants/params';
import { flattenPostsToGenericPostDetails } from '@/features/posts/utils/flattenPostsToGenericPostDetails';

const TICKER_POST_TYPES = new Set<GetAllPostsByGeoAreasTypesEnum>([
  GetAllPostsByGeoAreasTypesEnum.NewsItem,
  GetAllPostsByGeoAreasTypesEnum.Gossip,
  GetAllPostsByGeoAreasTypesEnum.Happening,
  GetAllPostsByGeoAreasTypesEnum.SpecialPost
]);
const TICKER_FORBIDDEN_ORGANIZATION_TAGS = [
  GetAllPostsByGeoAreasForbiddenOrganizationTagsEnum.EmergencyAid
];

const TICKER_START_TIME = LAST_3_DAYS;

export const useGetPostsByGeoArea = (params: {
  geoAreaId: string | undefined;
  count?: number;
  enabled?: boolean;
}) => {
  const { isAuthenticated } = useAuth();
  const api = useGrapevinePostApi();
  const count = params.count ?? 10;
  const enabled = params.enabled ?? true;

  return useQuery({
    queryKey: [
      API_CACHE_KEYS.POSTS_BY_GEO_AREA,
      { geoAreaId: params.geoAreaId, count, startTime: TICKER_START_TIME }
    ],
    queryFn: () =>
      api.getAllPostsByGeoAreas({
        geoAreaIds: new Set([params.geoAreaId!]),
        count,
        types: TICKER_POST_TYPES,
        forbiddenOrganizationTags: TICKER_FORBIDDEN_ORGANIZATION_TAGS,
        startTime: TICKER_START_TIME,
        sortBy: GetAllPostsByGeoAreasSortByEnum.Created
      }),
    enabled: isAuthenticated && enabled && Boolean(params.geoAreaId),
    staleTime: 0,
    select: useCallback(
      (data: PagedModelClientPost) =>
        flattenPostsToGenericPostDetails(data.content),
      []
    )
  });
};
